import React, { useEffect, useState } from 'react';
import { ImpulseSpinner } from '~Components/ImpulseSpinner';
import QRCodeRaw from 'react-qr-code';
import * as actions from '~actions';
import Button from '../../Scenes/Tables/Components/Button';
import {
  useAdyen,
  useButton,
  useDeviceInfo,
  useDispatch,
  useI18n,
  useLogger,
  useSelector,
} from '~Hooks';
import { BlueButton, Box, RedButton } from '~Styles';
import AdyenWizard from '~Components/Wizards/AdyenWizard';
import { useStorage } from '~Components/Storage';
import CancelButton from './CancelButton';
import styled from '@emotion/styled';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Icon from '~ComponentsImproved/Icon';
import CancelFilledIcon from '~Assets/icons/cancel-filled.svg';
import CheckFilledIcon from '~Assets/icons/check-filled.svg';
import { keyframes } from '@emotion/react';
import { capitalize } from 'lodash';

const PaymentStatusWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;

  margin-bottom: 16px;

  min-height: 112px;

  & > * {
    color: inherit;
  }
`;

const scaleUp = keyframes`
  from {
    transform: scale(0.5);
  }
  to {
    transform: scale(1);
  }
`;

const AnimatedIcon = styled(Icon)`
  animation: ${scaleUp} 0.3s ease-out;
`;

const SpinnerWrapper = styled(Box)`
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const QRCode = styled(QRCodeRaw)`
  margin-top: 30px;
  border: 25px solid var(--colorWhite);
  background: var(--colorWhite);
`;

/**
 * Joins the given parts with spaces, capitalizes the first letter and lowercases the rest,
 * so all status texts in the UI are formatted the same way, e.g. "Payment cancelled".
 */
const statusText = (...parts: string[]) => capitalize(parts.join(' '));

type PaymentStatusProps = {
  status: 'PENDING' | 'FAILED' | 'COMPLETED';
  paymentId: string;
  isSplitting?: boolean;
  retryRefund?: () => void;
  isRefunding?: boolean;
  textColor?: 'colorWhite' | 'colorBlack';
  onlyShowStatus?: boolean;
};

const PaymentStatus = ({
  status,
  paymentId,
  isSplitting,
  retryRefund,
  isRefunding,
  textColor = 'colorWhite',
  onlyShowStatus,
}: PaymentStatusProps) => {
  const logger = useLogger('payment-status');
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const { abortPayment } = useAdyen();
  const [storage] = useStorage();
  const deviceInfo = useDeviceInfo();
  const adyenTerminals = storage.adyenTerminals;
  const [isAborting, setIsAborting] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const payment = useSelector((state) => state.POSPayments.find((p) => p.id === paymentId));

  useEffect(() => {
    if (!payment?.hasConnectionIssues || showHelp) {
      return;
    }

    logger.info('Payment has connection issues', { payment });
    setShowHelp(true);
  }, [payment?.hasConnectionIssues, showHelp]);

  const cancelPayment = (forceFail?: boolean) => {
    if (forceFail) {
      dispatch({
        type: actions.POS_PAYMENT_FAILED,
        id: paymentId,
        error: i18n('PaymentStatus.Cancelled'),
      });
      return;
    }

    if (payment?.method === 'ADYEN' && payment.meta.serviceId && payment.variant) {
      setIsAborting(true);
      abortPayment({ serviceId: payment.meta.serviceId, terminalId: payment.variant }).then(
        ({ success }) => {
          logger.info('Aborting payment', { success });
          // If unsuccessful, activate button for retrying
          if (!success) {
            setIsAborting(false);
          }
        },
      );
    } else if (
      payment?.method === 'CRYPTO' ||
      payment?.method === 'SWISH_QR' ||
      payment?.method === 'KLARNA' ||
      payment?.method === 'LOOMIS'
    ) {
      setIsAborting(true);
      dispatch(actions.updatePaymentMeta(paymentId, { cancelRequested: true }));
    }
  };

  const addTerminalButton = useButton({ onClick: () => setShowWizard(true) });
  const cancelNoTerminalButton = useButton({ onClick: () => cancelPayment(true) });

  if (!status) {
    return null;
  }

  if (payment?.method === 'PAY_LATER') {
    return (
      <PaymentStatusWrapper color={textColor}>
        <ImpulseSpinner size={50} />
      </PaymentStatusWrapper>
    );
  }

  const actionType = isRefunding ? i18n('PaymentStatus.Refund') : i18n('PaymentStatus.Payment');

  if (status === 'PENDING' && payment?.method === 'ADYEN' && adyenTerminals.length === 0) {
    return (
      <PaymentStatusWrapper color={textColor}>
        <Box marginBottom="small" style={{ textAlign: 'center' }} padding="small">
          <Paragraph level={2} color="var(--colorWhite)">
            {i18n('PaymentStatus.NoTerminal')}
          </Paragraph>
        </Box>

        <Box display="flex" flexDirection="column" justifyContent="center">
          <BlueButton {...addTerminalButton} marginBottom="small">
            {i18n('PaymentStatus.NoTerminal.Add')}
          </BlueButton>
          <RedButton {...cancelNoTerminalButton}>
            {i18n('PaymentStatus.NoTerminal.Cancel')}
          </RedButton>
        </Box>

        {showWizard && (
          <AdyenWizard
            close={() => setShowWizard(false)}
            cancelPayment={() => cancelPayment(true)}
          />
        )}
      </PaymentStatusWrapper>
    );
  }

  if (status === 'PENDING') {
    return (
      <PaymentStatusWrapper color={textColor}>
        <SpinnerWrapper>
          <ImpulseSpinner size={50} />
        </SpinnerWrapper>

        <div className="animate-pulse">
          <Paragraph level={2} color="var(--colorWhite)">
            {statusText(actionType, i18n('PaymentStatus.InProgress'))}
          </Paragraph>
        </div>

        {payment && payment.meta.qr && !onlyShowStatus && (
          <>
            <QRCode size={150} value={payment.meta.qr} />
            <CancelButton
              onClick={() => cancelPayment(false)}
              background="var(--colorUIRed)"
              disabled={isAborting}>
              {isAborting ? i18n('Adyen.PleaseWait') : i18n('General.Cancel')}
            </CancelButton>
          </>
        )}

        {showHelp && (
          <Box marginTop="medium" style={{ width: '200px' }}>
            <Button
              slim
              indented
              onTouchStart={() => {
                setShowWizard(true);
                logger.info('Payment has connection issues, user clicked help', { payment });
              }}>
              {i18n('PaymentStatus.NeedHelp')}
            </Button>
          </Box>
        )}

        {payment?.meta.terminalStatus && (
          <Box marginTop="medium" style={{ width: '200px', textAlign: 'center' }}>
            {i18n(`Adyen.TerminalStatus.${payment.meta.terminalStatus}`)}
          </Box>
        )}

        {payment && ['ADYEN'].includes(payment.method) && !deviceInfo?.isAdyenTerminal && (
          <CancelButton
            onClick={cancelPayment}
            background="var(--colorUIRed)"
            disabled={isAborting}>
            {i18n('General.Cancel')}
          </CancelButton>
        )}

        {showWizard && (
          <AdyenWizard
            close={() => setShowWizard(false)}
            cancelPayment={() => cancelPayment(true)}
          />
        )}
      </PaymentStatusWrapper>
    );
  }

  if (status === 'FAILED') {
    return (
      <>
        <PaymentStatusWrapper color={textColor}>
          <AnimatedIcon
            src={CancelFilledIcon}
            color="var(--colorUIRed)"
            width="48px"
            height="48px"
          />
          <Box marginBottom="medium" style={{ textAlign: 'center' }}>
            <Paragraph level={2} color="var(--colorWhite)">
              {statusText(actionType, i18n('PaymentStatus.Cancelled'))}
            </Paragraph>

            {payment?.message && (
              <Paragraph level={5} top={8} color="var(--colorWhite)">
                {payment.message}
              </Paragraph>
            )}
          </Box>
        </PaymentStatusWrapper>

        {isSplitting && isRefunding && retryRefund && (
          <Button onTouchStart={retryRefund}>{i18n('PaymentStatus.RetryRefund')}</Button>
        )}
      </>
    );
  }

  return (
    <PaymentStatusWrapper color={textColor}>
      <AnimatedIcon
        src={CheckFilledIcon}
        color="var(--colorStatusGreen)"
        width="48px"
        height="48px"
      />

      <Paragraph level={2} color="var(--colorWhite)">
        {isSplitting
          ? statusText(i18n('PaymentStatus.ReadyForNext'), actionType.toLowerCase())
          : statusText(actionType, i18n('PaymentStatus.Completed'))}
      </Paragraph>
    </PaymentStatusWrapper>
  );
};

export default PaymentStatus;
